import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, Receipt } from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ it, kind, onAct }) => (
  <div data-testid={`flag-${kind}-${it.id}`} className="flex items-center gap-3 bg-slate-900/50 border border-white/10 rounded-lg px-3 py-2">
    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${kind === "receipt" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>{kind === "receipt" ? "Receipt missing" : "Needs approval"}</span>
    <span className="flex-1 min-w-0 text-sm text-slate-200 truncate">{it.title}{it.biz_category ? <span className="text-slate-500"> · {it.biz_category}</span> : ""}</span>
    <span className="font-mono text-sm text-amber-200 shrink-0">{money(it.amount)}</span>
    <button data-testid={`flag-${kind}-action-${it.id}`} onClick={() => onAct(it)} className="shrink-0 h-7 px-2.5 rounded border border-white/15 text-xs text-slate-200 hover:border-emerald-400/60 hover:text-emerald-300 transition-colors">{kind === "receipt" ? "Mark receipt attached" : "Approve"}</button>
  </div>
);

export default function PolicyFlags({ refreshKey }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/expenses/receipt-flags").then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data || (data.count === 0 && !(data.approvals || []).length)) return null;
  const act = (kind) => async (it) => {
    try {
      await api.post(`/expenses/${it.id}/${kind === "receipt" ? "receipt" : "approve"}`);
      toast.success(kind === "receipt" ? `Receipt marked for ${it.title}` : `${it.title} approved`);
      load();
    } catch (e) { toast.error("Could not update expense"); }
  };
  return (
    <div data-testid="snap-policy-flags" className="bg-[#1a0d10] border border-rose-500/30 rounded-2xl p-5 sm:p-6">
      <h2 className="text-lg font-bold text-white flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-400" /> Policy Flags</h2>
      <p className="text-xs text-slate-500 mt-1 mb-4">From your spend policies: receipts required at {money(data.threshold)}+{data.approval_threshold > 0 ? `, approval at ${money(data.approval_threshold)}+` : ""}.</p>
      {data.count > 0 && (
        <div className="space-y-1.5 mb-4">
          <p className="text-[10px] font-bold tracking-wider uppercase text-rose-300 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> {data.count} missing receipt{data.count === 1 ? "" : "s"} · {money(data.total)}</p>
          {data.items.map((it) => <Row key={it.id} it={it} kind="receipt" onAct={act("receipt")} />)}
        </div>
      )}
      {(data.approvals || []).length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold tracking-wider uppercase text-amber-300 flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> {data.approvals.length} awaiting approval</p>
          {data.approvals.map((it) => <Row key={it.id} it={it} kind="approval" onAct={act("approval")} />)}
        </div>
      )}
    </div>
  );
}
